import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ApiError, api, type AgentDetail, type Block, type ClientFollowup, type Leaf } from "../lib/api";
import { FollowupCard } from "../components/FollowupCard";
import { ReviewStep } from "../components/ReviewStep";

const REVIEW_KEY = "__review__";
import { FieldInput } from "../components/FieldInput";
import { ToolCard } from "../components/EstoqueWidget";
import { ChannelList } from "../components/Channels";
import { TestChat } from "../components/TestChat";
import { Badge, Modal, btnGhost, btnPrimary, card, toast } from "../components/ui";
import { agentRoute } from "../main";

// Agente do cliente. Configuração = BLOCO (lista da esquerda, página) →
// CÉLULA (card da direita) = um CAMPO EDITÁVEL (o que ele preenche) ou uma tool.
// Métricas / Ferramentas / Testar: em branco por enquanto (fases futuras).
// Conexões: fase 3 (canais da conta vinculados a este agente).

export type AgentTab = "metricas" | "ferramentas" | "testar" | "conexoes";
const TABS: { key: AgentTab | undefined; label: string }[] = [
  { key: undefined, label: "Configuração" }, { key: "metricas", label: "Métricas" }, { key: "ferramentas", label: "Ferramentas" }, { key: "testar", label: "Testar" }, { key: "conexoes", label: "Conexões" },
];

const fieldsOf = (b: Block) => b.rows.flat().flatMap((c) => (c.kind === "field" ? [c.field] : c.kind === "group" ? c.rows.flat().flatMap((x) => (x.kind === "field" ? [x.field] : [])) : []));
const leaves = (b: Block): Leaf[] => b.rows.flat().flatMap((c) => (c.kind === "group" ? c.rows.flat() : [c]));
// Feito = toda célula avaliável está ok. Campo: respondido. Tool com estoque: conectado.
// Follow-up: ligado. Tool sem estoque e sem campos editáveis não entra na conta.
// Bloco sem nada avaliável nunca fica verde (era every([]) = true).
const filled = (b: Block, answers: Record<string, any>, fu: ClientFollowup | null) => {
  const checks = leaves(b).flatMap((c): boolean[] => {
    if (c.kind === "field") { const v = answers[c.field.id] ?? c.field.default; return [Array.isArray(v) ? v.length > 0 : v !== "" && v != null]; }
    if (c.kind === "followup") return [!!fu?.enabled];
    if (c.estoque) return [c.estoque.state === "connected"];
    return c.editable?.length ? [true] : [];
  });
  return checks.length > 0 && checks.every(Boolean);
};
const hasNew = (b: Block, newIds: Set<string>) => fieldsOf(b).some((f) => newIds.has(f.id));

const Check = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>;

function Empty({ text }: { text: string }) {
  return <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--ink-muted)", border: "1px dashed var(--line-strong)", borderRadius: 10, fontSize: 13 }}>{text}</div>;
}

export function AgentPage() {
  const { agentId } = agentRoute.useParams();
  const { tab } = agentRoute.useSearch();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: agent, isLoading, error } = useQuery<AgentDetail>({ queryKey: ["agent", agentId], queryFn: () => api.get(`/agents/${agentId}`) });

  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [followup, setFollowup] = useState<ClientFollowup | null>(null);  // célula "Follow-up" (config do agente)
  // Edições dos campos das tools globais: {tool_id: {path: valor}}; os valores atuais já vêm mesclados no GET.
  const [overrides, setOverrides] = useState<Record<string, Record<string, any>>>({});
  const [saved, setSaved] = useState(() => JSON.stringify([{}, null]));  // mesma forma de [answers, followup] antes do load
  const [blockKey, setBlockKey] = useState<string | null>(null);
  const [publishStep, setPublishStep] = useState<0 | 1 | 2>(0);
  const [confirmDel, setConfirmDel] = useState(false);
  const del = useMutation({
    mutationFn: () => api.delete(`/agents/${agentId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); toast("Agente excluído"); nav({ to: "/" }); },
    onError: (e) => toast((e as ApiError).detail ?? "Não foi possível excluir"),
  });
  useEffect(() => {
    if (!agent) return;
    setAnswers(agent.answers);
    setFollowup(agent.followup);
    setOverrides({});
    setSaved(JSON.stringify([agent.answers, agent.followup]));
  }, [agent]);
  const dirty = JSON.stringify([answers, followup]) !== saved || Object.keys(overrides).length > 0;
  // follows com tempo inválido (zero ou não crescente) não vão pro servidor: Salvar/Publicar travam
  const fuInvalid = !!followup?.enabled && followup.steps.some((s, i) => s.after_minutes < 1 || (i > 0 && s.after_minutes <= followup.steps[i - 1].after_minutes));
  const patchBody = () => ({ answers, ...(followup ? { followup } : {}), ...(Object.keys(overrides).length ? { tool_overrides: overrides } : {}) });
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => api.patch(`/agents/${agentId}`, patchBody()),
    onSuccess: () => { setSaved(JSON.stringify([answers, followup])); qc.invalidateQueries({ queryKey: ["agent", agentId] }); qc.invalidateQueries({ queryKey: ["agents"] }); toast("Rascunho salvo"); },
    onError: (e) => toast((e as ApiError).detail ?? "Erro ao salvar"),
  });
  const publish = useMutation({
    mutationFn: async () => { if (dirty) await api.patch(`/agents/${agentId}`, patchBody()); return api.post(`/agents/${agentId}/publish`, {}); },
    onSuccess: () => { setSaved(JSON.stringify([answers, followup])); setPublishStep(0); qc.invalidateQueries({ queryKey: ["agent", agentId] }); qc.invalidateQueries({ queryKey: ["agents"] }); toast("Publicado ✓"); },
    onError: (e) => { setPublishStep(0); toast((e as ApiError).detail ?? "Erro ao publicar"); },
  });

  if (isLoading) return <div style={{ padding: 32, color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>;
  if (error || !agent) return <div style={{ padding: 32 }}><Empty text="Agente não encontrado." /></div>;

  const blocks = agent.blocks;
  const newIds = new Set(agent.new_field_ids);  // some após Salvar (refetch traz a lista vazia)
  const review = blockKey === REVIEW_KEY;  // última etapa fixa: Conferir e publicar
  const block = review ? undefined : blocks.find((b) => b.key === blockKey) ?? blocks[0];
  const idx = block ? blocks.indexOf(block) : -1;
  const status = dirty || !agent.published ? <Badge tone="warn">Em rascunho</Badge>
    : agent.has_unpublished_changes ? <Badge tone="warn">Alterações não publicadas</Badge> : <Badge tone="ok">Publicado</Badge>;

  return (
    <div style={{ padding: "0 32px 60px" }}>
      {/* barra fixa */}
      <div style={{ position: "sticky", top: 0, zIndex: "var(--z-sticky)" as any, background: "var(--surface-app)", padding: "24px 0 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid var(--line)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: "var(--ink-dim)" }}><Link to="/" style={{ color: "var(--ink-muted)", textDecoration: "none" }}>Agentes</Link> / {agent.name}</div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, letterSpacing: "-.01em", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.name}</h1>
        </div>
        {status}
        <button style={{ ...btnGhost, color: "var(--danger)" }} onClick={() => setConfirmDel(true)}>Excluir</button>
        {dirty && <button style={btnGhost} disabled={save.isPending || fuInvalid} title={fuInvalid ? "Corrija os tempos dos follows" : undefined} onClick={() => save.mutate()}>{save.isPending ? "Salvando…" : "Salvar rascunho"}</button>}
        <button style={btnPrimary} disabled={fuInvalid} title={fuInvalid ? "Corrija os tempos dos follows" : undefined} onClick={() => setPublishStep(1)}>Publicar</button>
      </div>
      {confirmDel && (
        <Modal title="Excluir agente?" onClose={() => setConfirmDel(false)}>
          <div style={{ fontSize: 13, color: "var(--ink-secondary)" }}>Apaga <b>{agent.name}</b> com todas as conversas e configurações. A conexão de WhatsApp vinculada fica livre. Não dá pra desfazer.</div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button style={btnGhost} onClick={() => setConfirmDel(false)}>Cancelar</button>
            <button style={{ ...btnPrimary, background: "var(--danger)" }} disabled={del.isPending} onClick={() => del.mutate()}>{del.isPending ? "Excluindo…" : "Excluir"}</button>
          </div>
        </Modal>
      )}

      {/* abas */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--line)", marginBottom: 20 }}>
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button key={t.label} onClick={() => nav({ to: "/agentes/$agentId", params: { agentId }, search: t.key ? { tab: t.key } : {} })}
              style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500, background: "none", border: "none", borderBottom: `2px solid ${on ? "var(--brand)" : "transparent"}`, marginBottom: -1, color: on ? "var(--brand)" : "var(--ink-muted)", cursor: "pointer" }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "metricas" && <Empty text="Métricas do agente em breve." />}
      {tab === "ferramentas" && <Empty text="Ferramentas em breve." />}
      {tab === "testar" && <TestChat agentId={agentId} />}
      {tab === "conexoes" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)" }}>Canais da sua conta. Marque os que atendem com este agente. Um canal atende com um agente por vez.</p>
          <ChannelList agentId={agentId} />
        </div>
      )}

      {!tab && (
        blocks.length === 0 ? <Empty text="Este agente ainda não tem configurações liberadas. Fale com o suporte." /> : (
          <div className="ss-page">
            {/* Etapas: número + título, sem subtítulo. Feito = check verde; atual = vermelho. */}
            <nav aria-label="Etapas" style={{ position: "sticky", top: 96, display: "flex", flexDirection: "column", gap: 6 }}>
              {blocks.map((b, i) => {
                const on = b.key === block?.key;
                const ok = filled(b, answers, followup);
                return (
                  <button key={b.key} onClick={() => setBlockKey(b.key)} aria-current={on ? "step" : undefined}
                    style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: on ? "var(--surface-card)" : "transparent", border: `1px solid ${on ? "var(--line)" : "transparent"}`, boxShadow: on ? "var(--shadow-pop)" : undefined }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0, background: on ? "var(--brand)" : ok ? "var(--ok-soft)" : "var(--surface-hover)", color: on ? "#fff" : ok ? "var(--ok)" : "var(--ink-muted)" }}>
                      {ok && !on ? <Check /> : i + 1}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: on ? 600 : 500, color: on ? "var(--ink)" : "var(--ink-secondary)", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title || b.key}</span>
                      {hasNew(b, newIds) && <i title="Campos novos" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--ok)", flexShrink: 0 }} />}
                    </span>
                  </button>
                );
              })}
              <button onClick={() => setBlockKey(REVIEW_KEY)} aria-current={review ? "step" : undefined}
                style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", background: review ? "var(--surface-card)" : "transparent", border: `1px solid ${review ? "var(--line)" : "transparent"}`, boxShadow: review ? "var(--shadow-pop)" : undefined }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0, background: review ? "var(--brand)" : "var(--surface-hover)", color: review ? "#fff" : "var(--ink-muted)" }}>{blocks.length + 1}</span>
                <span style={{ fontSize: 14, fontWeight: review ? 600 : 500, color: review ? "var(--ink)" : "var(--ink-secondary)" }}>Conferir e publicar</span>
              </button>
            </nav>

            {review ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div aria-hidden style={{ height: 4, borderRadius: 99, background: "var(--brand)" }} />
                <ReviewStep blocks={blocks} answers={answers} followup={followup} dirty={dirty} publishing={publish.isPending}
                  onGoTo={setBlockKey} onBack={() => setBlockKey(blocks[blocks.length - 1].key)} onPublish={() => setPublishStep(1)} />
              </div>
            ) : block && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div aria-hidden style={{ height: 4, borderRadius: 99, background: "var(--line)", overflow: "hidden" }}>
                  <div style={{ width: "100%", height: "100%", background: "var(--brand)", transformOrigin: "left", transform: `scaleX(${(idx + 1) / (blocks.length + 1)})`, transition: "transform .3s" }} />
                </div>
                <div style={{ ...card, padding: "28px 32px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--brand)" }}>Etapa {idx + 1} de {blocks.length + 1}</div>
                    <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 600, marginTop: 6, letterSpacing: "-.01em" }}>{block.title || block.key}</h2>
                    {block.description && <p style={{ marginTop: 6, fontSize: 14, color: "var(--ink-muted)", lineHeight: 1.5 }}>{block.description}</p>}
                  </div>
                  {(() => {
                    // Miolo de uma célula (campo ou tool). Campo avulso vive direto no card da etapa;
                    // dentro do sub-bloco é a mesma coisa, só que num painel mais suave.
                    const panel: React.CSSProperties = { background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 18 };
                    const cell = (c: Leaf) => {
                      const span = { minWidth: 0, gridColumn: `span ${c.width / 25}` };
                      if (c.kind === "followup") {
                        return (
                          <div key={c.id} style={{ ...panel, ...span }}>
                            {followup && <FollowupCard agentId={agentId} value={followup} onChange={setFollowup} />}
                          </div>
                        );
                      }
                      if (c.kind === "tool") {
                        return (
                          <div key={c.id} style={{ ...panel, ...span }}>
                            <div style={{ paddingBottom: 12, borderBottom: "1px solid var(--line)" }}>
                              <h3 style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</h3>
                              {c.description && <p style={{ marginTop: 3, fontSize: 13, color: "var(--ink-muted)" }}>{c.description}</p>}
                            </div>
                            <ToolCard agentId={agentId} s={c} overrides={overrides[c.id.slice(5)] ?? {}}
                              onOverride={(path, v) => setOverrides((p) => ({ ...p, [c.id.slice(5)]: { ...(p[c.id.slice(5)] ?? {}), [path]: v } }))} />
                          </div>
                        );
                      }
                      const f = c.field;
                      const isNew = newIds.has(f.id);
                      return (
                        <div key={c.id} className={isNew ? "ss-new" : undefined} style={{ ...span, display: "flex", flexDirection: "column", gap: 8 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-secondary)", display: "flex", alignItems: "center", gap: 8 }}>
                            {f.label || f.key}{isNew && <Badge tone="new">Novo</Badge>}
                          </label>
                          {f.description && <div style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.5, marginTop: -4 }}>{f.description}</div>}
                          <FieldInput f={f} value={answers[f.id] ?? f.default} onChange={(v) => setAnswers((p) => ({ ...p, [f.id]: v }))}
                            other={answers[`${f.id}:outro`] ?? ""} onOther={(v) => setAnswers((p) => ({ ...p, [`${f.id}:outro`]: v }))} />
                        </div>
                      );
                    };
                    return block.rows.map((row, i) => (
                      <div key={i} className="ss-row" style={{ gap: 18 }}>
                        {row.map((c) => c.kind !== "group" ? cell(c) : (
                          <div key={c.id} style={{ ...panel, minWidth: 0, gridColumn: "span 4", display: "flex", flexDirection: "column", gap: 18 }}>
                            {(c.title || c.description) && (
                              <div>
                                {c.title && <h3 style={{ fontSize: 15, fontWeight: 600 }}>{c.title}</h3>}
                                {c.description && <p style={{ marginTop: 3, fontSize: 13, color: "var(--ink-muted)" }}>{c.description}</p>}
                              </div>
                            )}
                            {c.rows.map((r, j) => <div key={j} className="ss-row" style={{ gap: 18 }}>{r.map((x) => cell(x))}</div>)}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                  <div style={{ borderTop: "1px solid var(--line)", paddingTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <button style={{ ...btnGhost, opacity: idx <= 0 ? 0.4 : 1 }} disabled={idx <= 0} onClick={() => setBlockKey(blocks[idx - 1].key)}>Voltar</button>
                    <button style={btnPrimary} onClick={() => setBlockKey(idx < blocks.length - 1 ? blocks[idx + 1].key : REVIEW_KEY)}>Continuar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {publishStep === 1 && (
        <Modal title="Publicar alterações?" onClose={() => setPublishStep(0)}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.5 }}>As mudanças de configuração passam a valer pra todas as conversas novas desse agente.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button style={btnGhost} onClick={() => setPublishStep(0)}>Cancelar</button>
            <button style={btnPrimary} onClick={() => setPublishStep(2)}>Continuar</button>
          </div>
        </Modal>
      )}
      {publishStep === 2 && (
        <Modal title="Tem certeza?" onClose={() => setPublishStep(0)}>
          <p style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: 1.5 }}>Isso vai pra produção agora. Dá pra ajustar e publicar de novo depois.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button style={btnGhost} onClick={() => setPublishStep(0)}>Cancelar</button>
            <button style={btnPrimary} disabled={publish.isPending} onClick={() => publish.mutate()}>{publish.isPending ? "Publicando…" : "Publicar agora"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
